import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/ui/command';
import { Button } from '@shared/ui/button';
import { SuiteIcon } from '@shared/icons';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';
import { useRecentPages } from '@shared/activity';
import { useIsMobile } from '@shared/hooks/use-breakpoint';
import { useMobileShell } from '@shared/shell';

export const GlobalSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { hasPermission, enabledSuites } = useAuth();
  const recentPages = useRecentPages();
  const isMobile = useIsMobile();
  const { openSearch } = useMobileShell();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (isMobile) {
          openSearch();
        } else {
          setOpen((prev) => !prev);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, openSearch]);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleButtonClick = () => {
    if (isMobile) {
      openSearch();
    } else {
      setOpen(true);
    }
  };

  const permittedSuites = allSuites.filter((s) => {
    if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(s.id)) return false;
    return s.requiredPermission ? hasPermission(s.requiredPermission) : true;
  });

  return (
    <>
      <Button
        variant="ghost"
        onClick={handleButtonClick}
        className="h-8 px-2.5 text-xs text-navbar-muted hover:text-navbar-foreground hover:bg-navbar-accent gap-2 border border-navbar-border/60 rounded-md"
        aria-label="Search across platform"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="pointer-events-none hidden md:inline-flex h-4 select-none items-center gap-1 rounded bg-white/10 px-1.5 font-mono text-[10px] font-medium text-navbar-muted">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a suite, page, or search query..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {recentPages.length > 0 && (
            <CommandGroup heading="Recent Pages">
              {recentPages.slice(0, 5).map((page) => (
                <CommandItem key={page.key} onSelect={() => handleSelect(page.path)}>
                  <SuiteIcon name={page.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{page.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground font-mono">{page.path}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandSeparator />

          <CommandGroup heading="Suites">
            {permittedSuites.map((suite) => (
              <CommandItem key={suite.id} onSelect={() => handleSelect(suite.basePath)}>
                <SuiteIcon name={suite.icon} className="mr-2 h-4 w-4 text-primary" />
                <span>{suite.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Pages">
            {permittedSuites.flatMap((suite) =>
              suite.nav
                .filter((nav) => (nav.permission ? hasPermission(nav.permission) : true))
                .map((nav) => (
                  <CommandItem key={`${suite.id}:${nav.path}`} onSelect={() => handleSelect(nav.path)}>
                    <SuiteIcon name={nav.icon} className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{nav.label}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{suite.name}</span>
                  </CommandItem>
                ))
            )}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};
